import _ from 'lodash'
import type { SiteScopeParams, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type { Data, PageJson, Topology, TPAWidgetDataItem, WidgetRefDataItem } from '@wix/thunderbolt-becky-types'
import type {
	BeckyRequestPayload,
	RefFunctionLibrary,
	WidgetTemplatesStore,
	WidgetTemplatesStoreByViewMode,
} from '@wix/thunderbolt-becky-root'
import { flattenStructure } from '@wix/thunderbolt-becky-root'
import { REMOTE_REF_DEAD_COMP } from './constants'
import { BaseDataItem } from '@wix/thunderbolt-becky-types'
import { getRefTemplateKey } from './byRefsis/byRefUtils'

export const RemoteWidgetStructureBuilderPath = `pages/pages/remoteWidgetStructureBuilder`

const REQUEST_PARAMS = [
	'isEditorMode',
	'viewMode',
	'ck',
	'experiments',
	'isHttps',
	'isUrlMigrated',
	'quickActionsMenuEnabled',
	'v',
	'dfVersion',
]
const MODULE_NAME = 'remote-widget-structure-builder'

const objToQueryParams = (obj: Record<string, string>) => {
	return Object.keys(obj)
		.map((key) => `${key}=${obj[key]}`)
		.join('&')
}

const extractSiteAssetsParams = (str: string) =>
	_.chain(str)
		.split('?')
		.last()
		.split('&')
		.map((x) => x.replace('}', '').replace('{', '').split('='))
		.fromPairs()
		.value()

// TODO this function does a lot of work per widget url built where in reality it could do it once per site - we should offload the work to createRemoteRefFetcher once byRefV2 is merged
export const buildWidgetUrl = (
	widgetDataItem: WidgetRefDataItem,
	clientSpecMap: ViewerClientSpecMap,
	{ siteAssetsServerUrl }: Topology,
	appDefinitionIdToSiteRevision: SiteScopeParams['appDefinitionIdToSiteRevision'],
	requestPayload: BeckyRequestPayload,
	keepWixCodeConnections: any
) => {
	const { appDefinitionId, widgetId, variationId = '' } = widgetDataItem
	const appData = _.find(clientSpecMap, { appDefinitionId })

	const remoteWidgetPageId = _.get(appData, ['widgets', widgetId, 'componentFields', 'appStudioFields', 'id'])

	const siteAssetsData = _.get(appData, ['appFields', 'platform', 'baseUrls', 'siteAssets'])
	const { siteId, metaSiteId, siteRevision: siteRevisionFromRoot } = extractSiteAssetsParams(siteAssetsData)

	const siteRevision = appDefinitionIdToSiteRevision
		? _.get(appDefinitionIdToSiteRevision, [appDefinitionId], siteRevisionFromRoot)
		: siteRevisionFromRoot

	const requestParams = _.pick(requestPayload, REQUEST_PARAMS) as Record<string, string>
	const params = {
		appDefId: appDefinitionId,
		devCenterWidgetId: widgetId,
		variationId,
		pageCompId: variationId ? variationId : remoteWidgetPageId,
		siteId,
		metaSiteId,
		siteRevision,
		module: MODULE_NAME,
		moduleVersion: requestPayload.remoteWidgetStructureBuilderVersion,
		keepWixCodeConnections,
		...requestParams,
	}

	return `${siteAssetsServerUrl}/${RemoteWidgetStructureBuilderPath}?${objToQueryParams(params)}`
}

const safeParseJson = (val?: string) => {
	if (!val) {
		return val
	}

	try {
		return JSON.parse(val)
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('parseJSON failed parsing value:', val)
		}
		return null
	}
}

type FetchRemoteRef = (widgetDataItem: WidgetRefDataItem) => Promise<WidgetTemplatesStore[string]>

const isTPAWidgetDataItem = (dataItem: BaseDataItem): dataItem is TPAWidgetDataItem => dataItem.type === 'TPAWidget'

const applyApplicationId = (data: PageJson['data'], appDefinitionIdToApplicationId: Record<string, string>) =>
	_.forEach(data.document_data, (dataItem) => {
		if (isTPAWidgetDataItem(dataItem)) {
			dataItem.applicationId = `${appDefinitionIdToApplicationId[dataItem.appDefinitionId] ?? ''}`
		}
	})

const createRemoteRefFetcher = (
	fetchJSON: <Json extends {} = {}>(url: string) => Promise<Json | null>,
	clientSpecMap: ViewerClientSpecMap,
	topology: Topology,
	requestPayload: BeckyRequestPayload
): FetchRemoteRef => {
	const appDefinitionIdToSiteRevision: SiteScopeParams['appDefinitionIdToSiteRevision'] = requestPayload.appDefinitionIdToSiteRevision
		? safeParseJson(requestPayload.appDefinitionIdToSiteRevision)
		: null

	const appDefinitionIdToApplicationId: Record<string, string> = _(clientSpecMap)
		.mapKeys('appDefinitionId')
		.mapValues('applicationId')
		.value()

	return async (
		widgetDataItem: WidgetRefDataItem,
		keepWixCodeConnections: boolean = false
	): Promise<WidgetTemplatesStore[string]> => {
		const widgetUrl = buildWidgetUrl(
			widgetDataItem,
			clientSpecMap,
			topology,
			appDefinitionIdToSiteRevision,
			requestPayload,
			keepWixCodeConnections
		)

		const widgetJson = await fetchJSON<PageJson>(widgetUrl)
		if (!widgetJson) {
			// @ts-expect-error TODO fix dead ref type
			return REMOTE_REF_DEAD_COMP
		}

		// TODO is isMobile always false?!? in carmi we check root.get('viewMode').eq('')
		const isMobile = false

		const { data, structure, translations } = widgetJson

		applyApplicationId(data, appDefinitionIdToApplicationId)

		return {
			data,
			structure: flattenStructure(structure, isMobile, data.slots),
			translations,
			rootId: structure.id,
		}
	}
}

const createAsyncJsonFetcher = (fetchUrl: RefFunctionLibrary['fetchUrl']) => {
	const ERROR = 'ERROR'

	return <Ret = never>(url: string): Promise<Ret | null> =>
		new Promise<Ret | null>((resolve) => {
			fetchUrl(url, ERROR, (res) => {
				if (res === null) {
					// initial callback call by fnLib fetchUrl
					return
				}

				resolve(res === ERROR ? null : (safeParseJson(res) as Ret))
			})
		})
}

export const createRemoteRefsFetcher = (
	fetchUrl: RefFunctionLibrary['fetchUrl'],
	clientSpecMap: ViewerClientSpecMap,
	topology: Topology,
	requestPayload: BeckyRequestPayload,
	cache?: { [widgetKey: string]: true }
) => {
	const fetchRemoteRef = createRemoteRefFetcher(
		createAsyncJsonFetcher(fetchUrl),
		clientSpecMap,
		topology,
		requestPayload
	)

	const viewMode = requestPayload.viewMode!
	const widgets: WidgetTemplatesStoreByViewMode = { desktop: {}, mobile: {} }

	const fetchRemoteWidgetsRecursively = async (
		documentData: Data['document_data']
	): Promise<WidgetTemplatesStoreByViewMode> => {
		const widgetDataItems = _(documentData)
			.pickBy({ type: 'WidgetRef' })
			.values()
			.uniqBy((widgetDataItem) => getRefTemplateKey(widgetDataItem as WidgetRefDataItem))
			.reject((widgetDataItem) => {
				const templateKey = getRefTemplateKey(widgetDataItem as WidgetRefDataItem)
				return cache?.[templateKey] || templateKey in widgets[viewMode]
			})
			.value() as Array<WidgetRefDataItem>

		await Promise.all(
			widgetDataItems.map(async (widgetDataItem) => {
				const widgetKey = getRefTemplateKey(widgetDataItem)

				// @ts-expect-error Mark widget as being downloaded
				widgets[viewMode][widgetKey] = null

				const remoteRef = await fetchRemoteRef(widgetDataItem)
				widgets[viewMode][widgetKey] = remoteRef
				if (cache) {
					cache[widgetKey] = true
				}

				return fetchRemoteWidgetsRecursively(remoteRef.data.document_data)
			})
		)

		return widgets
	}

	return fetchRemoteWidgetsRecursively
}
